// ─────────────────────────────────────────────────────────────────────────────
// Parser puro de deep links — sem dependências de Angular/Capacitor, 100% testável.
// Quebra a URL inteira (https ou custom scheme) nas partes úteis do DeepLinkPayload.
// ─────────────────────────────────────────────────────────────────────────────

import { DeepLinkOrigin, DeepLinkPayload } from "./deeplink.types";

/** Hosts web verificados (Universal Link / App Link). Devem casar com os intent-filters
 *  do Android e com o Associated Domains do iOS.
 *  `orbitta-production.web.app` é o domínio padrão do Firebase Hosting do MESMO site de
 *  produção (mesmo build, mesmo `.well-known/`) — é o host que os e-mails/SMS de
 *  recuperação de senha usam hoje. */
export const KNOWN_WEB_HOSTS = new Set<string>([
  "orbitta.sorocabarefrescos.com.br",
  "www.orbitta.sorocabarefrescos.com.br",
  "orbitta-production.web.app",
]);

/**
 * Interpreta a URL inteira de um deep link.
 * Suporta:
 *  - Universal/App Links: https://orbitta.sorocabarefrescos.com.br/promo/x?utm=abc#sec
 *  - Custom scheme:        orbitta://tabs/home?ref=1
 * Lança se a URL for inválida (chamador deve tratar).
 */
export function parseDeepLinkUrl(url: string, origin: DeepLinkOrigin): DeepLinkPayload {
  const parsed = new URL(url);
  const scheme = parsed.protocol.replace(/:$/, "").toLowerCase();
  const isWeb = scheme === "https" || scheme === "http";
  const hostname = parsed.hostname ?? "";

  // Resolve o path lógico interno do app.
  // - Web (https://host/promo/x): path = pathname.
  // - Custom scheme (orbitta://promo/x): o "host" faz parte do caminho lógico.
  let path: string;
  if (isWeb) {
    path = parsed.pathname || "/";
  } else {
    const hostPart = hostname ? `/${hostname}` : "";
    path = `${hostPart}${parsed.pathname}` || "/";
  }
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }
  path = path.replace(/\/{2,}/g, "/"); // normaliza barras duplicadas
  if (path.length > 1 && path.endsWith("/")) {
    path = path.slice(0, -1); // remove barra final (exceto raiz)
  }

  const segments = path.split("/").filter((s) => s.length > 0);

  // Query params → objeto simples.
  const params: Record<string, string> = {};
  parsed.searchParams.forEach((value, key) => {
    params[key] = value;
  });

  const fragment = parsed.hash ? parsed.hash.replace(/^#/, "") : "";
  const route = `${path}${parsed.search}${parsed.hash}`;

  return {
    url,
    origin,
    scheme,
    host: isWeb ? parsed.host : "",
    route,
    path,
    segments,
    params,
    fragment,
    isVerifiedWebLink: isWeb && KNOWN_WEB_HOSTS.has(hostname),
  };
}
